import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { type QueryKey, useQueryClient } from "@msuite/ui/query";
import React from "react";
import { Can } from "@/components/citadel";
import { useProject } from "@/hooks/use-project";
import { AddProjectContactModal } from "@/modals/projects/add-project-contact-modal";

/** Props Interface */
interface ProjectContactsMenuProps {
	queryKey: QueryKey;
	projectId: string;
}

export function ProjectContactsMenu({ queryKey, projectId }: ProjectContactsMenuProps) {
	/** Hooks */
	const { t } = useTranslation();
	const disclosure = useDisclosure();
	const qc = useQueryClient();

	/** Query */
	// Assigning a contact writes `/projects/:projectId/contacts`, so the rule binds against this
	// site rather than the category: `update Project` also reaches only the sites its holder runs.
	const { project } = useProject({ projectId });

	/** Functions */
	async function refetch() {
		await qc.invalidateQueries({
			queryKey,
		});
	}

	/** Render */
	return (
		<React.Fragment>
			<Can
				action="update"
				subject="Project"
				resource={project ? { ...project } : undefined}
			>
				<Button
					variant="primary"
					icon={<Icon name="Plus" />}
					onClick={disclosure.onOpen}
					data-cy="add-project-contact-button"
				>
					{t("contacts.add_contact")}
				</Button>
			</Can>
			{disclosure.isOpen && (
				<AddProjectContactModal
					projectId={projectId}
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
					onComplete={refetch}
				/>
			)}
		</React.Fragment>
	);
}
